import React, { useState } from 'react';
import { api } from '../../api/client.ts';

interface SuQuanGameProps {
  onBackToHub: () => void;
}

interface WarlordProvince {
  id: string;
  name: string;
  warlordName: string;
  title: string;
  location: string;
  garrison: number;
  foodReserves: number;
  isConquered: boolean;
  avatar: string;
}

const INITIAL_PROVINCES: WarlordProvince[] = [
  { id: 'hoa_lu', name: 'Động Hoa Lư', warlordName: 'Đinh Bộ Lĩnh', title: 'Vạn Thắng Vương', location: 'Ninh Bình', garrison: 4500, foodReserves: 8000, isConquered: true, avatar: '👑' },
  { id: 'do_dong', name: 'Đỗ Động Giang', warlordName: 'Đỗ Cảnh Thạc', title: 'Đỗ Cảnh Công', location: 'Quốc Oai, Hà Nội', garrison: 3800, foodReserves: 5000, isConquered: false, avatar: '⚔️' },
  { id: 'phong_chau', name: 'Phong Châu', warlordName: 'Kiều Công Hãn', title: 'Kiều Tam Chế', location: 'Bạch Hạc, Phú Thọ', garrison: 3200, foodReserves: 4200, isConquered: false, avatar: '🛡️' },
  { id: 'bo_hai', name: 'Bố Hải Khẩu', warlordName: 'Trần Lãm', title: 'Trần Minh Công', location: 'Thái Bình', garrison: 4000, foodReserves: 7500, isConquered: false, avatar: '🌊' },
  { id: 'tay_phu', name: 'Tây Phù Liệt', warlordName: 'Nguyễn Siêu', title: 'Nguyễn Cảnh Công', location: 'Thanh Trì, Hà Nội', garrison: 3600, foodReserves: 4800, isConquered: false, avatar: '🏹' },
  { id: 'binh_kieu', name: 'Bình Kiều', warlordName: 'Ngô Xương Xí', title: 'Ngô Sứ Quân', location: 'Triệu Sơn, Thanh Hóa', garrison: 3500, foodReserves: 4600, isConquered: false, avatar: '🐎' },
  { id: 'hoi_ho', name: 'Hồi Hồ', warlordName: 'Kiều Thuận', title: 'Kiều Lệnh Công', location: 'Cẩm Khê, Phú Thọ', garrison: 2800, foodReserves: 3500, isConquered: false, avatar: '⛰️' },
  { id: 'te_giang', name: 'Tế Giang', warlordName: 'Lữ Đường', title: 'Lữ Tá Công', location: 'Văn Giang, Hưng Yên', garrison: 2900, foodReserves: 3800, isConquered: false, avatar: '🌾' },
  { id: 'dang_chau', name: 'Đằng Châu', warlordName: 'Phạm Bạch Hổ', title: 'Phạm Phòng Át', location: 'Kim Động, Hưng Yên', garrison: 3400, foodReserves: 4500, isConquered: false, avatar: '🐅' },
  { id: 'duong_lam', name: 'Đường Lâm', warlordName: 'Ngô Nhật Khánh', title: 'Ngô An Vương', location: 'Sơn Tây, Hà Nội', garrison: 3700, foodReserves: 5200, isConquered: false, avatar: '🦅' },
  { id: 'tam_dai', name: 'Tam Đái', warlordName: 'Nguyễn Khoan', title: 'Nguyễn Thái Bình', location: 'Vĩnh Tường, Vĩnh Phúc', garrison: 3100, foodReserves: 4100, isConquered: false, avatar: '🏕️' },
  { id: 'sieu_loai', name: 'Siêu Loại', warlordName: 'Lý Khuê', title: 'Lý Lãng Công', location: 'Thuận Thành, Bắc Ninh', garrison: 3300, foodReserves: 4400, isConquered: false, avatar: '⚡' },
];

export const SuQuanGame: React.FC<SuQuanGameProps> = ({ onBackToHub }) => {
  const [provinces, setProvinces] = useState<WarlordProvince[]>(INITIAL_PROVINCES);
  const [soldiers, setSoldiers] = useState<number>(5000);
  const [food, setFood] = useState<number>(7500);
  const [prestige, setPrestige] = useState<number>(85); // Uy danh
  const [selectedTarget, setSelectedTarget] = useState<WarlordProvince | null>(null);
  const [tacticsModal, setTacticsModal] = useState<boolean>(false);
  const [campaignLogs, setCampaignLogs] = useState<string[]>([
    'Vạn Thắng Vương Đinh Bộ Lĩnh phất cờ lau tại động Hoa Lư, mở đầu đại nghiệp định quốc an dân.',
  ]);

  const conqueredCount = provinces.filter((p) => p.isConquered).length;
  const isUnified = conqueredCount === 12;

  // Farm Grain
  const handleFarming = () => {
    const gainedFood = 1200 + conqueredCount * 250;
    setFood((f) => f + gainedFood);
    setCampaignLogs((l) => [
      `🌾 Mùa màng bội thu trên các châu quận quy phục! Tích trữ thêm +${gainedFood} thạch lương thảo.`,
      ...l.slice(0, 4),
    ]);
  };

  // Recruit Troops
  const handleRecruit = () => {
    if (food < 1500) {
      alert('Không đủ lương thảo để cấp dưỡng binh sĩ (cần ít nhất 1,500 thạch lương)!');
      return;
    }
    setFood((f) => f - 1500);
    setSoldiers((s) => s + 1200);
    setPrestige((p) => p + 3);
    setCampaignLogs((l) => [
      `🛡️ Chiêu mộ thêm +1,200 dũng sĩ (-1,500 Lương thảo). Binh lực Hoa Lư ngày càng hùng hậu!`,
      ...l.slice(0, 4),
    ]);
  };

  // Attack Province with Chosen Tactic
  const executeAttack = (tactic: 'frontal' | 'ambush' | 'siege') => {
    if (!selectedTarget) return;

    let lossRatio = 0.3;
    let winChance = 0.7;
    let tacticDesc = '';

    if (tactic === 'frontal') {
      tacticDesc = 'Đột kích vỗ mặt';
      winChance = soldiers >= selectedTarget.garrison * 1.2 ? 0.85 : 0.55;
      lossRatio = 0.35;
    } else if (tactic === 'ambush') {
      tacticDesc = 'Phục kích ven sông';
      winChance = prestige > 80 ? 0.8 : 0.6;
      lossRatio = 0.2;
    } else {
      tacticDesc = 'Vây thành bức hàng';
      if (food < 2000) {
        alert('Vây thành kéo dài cần ít nhất 2,000 thạch lương!');
        return;
      }
      setFood((f) => f - 2000);
      winChance = 0.9;
      lossRatio = 0.12;
    }

    const isSuccess = Math.random() < winChance;
    const lossSoldiers = Math.floor(selectedTarget.garrison * lossRatio);

    if (isSuccess) {
      setSoldiers((s) => Math.max(800, s - lossSoldiers));
      setPrestige((p) => p + 15);
      setFood((f) => f + Math.floor(selectedTarget.foodReserves * 0.7));

      setProvinces((prev) =>
        prev.map((pr) => (pr.id === selectedTarget.id ? { ...pr, isConquered: true } : pr))
      );

      const nextConquered = conqueredCount + 1;
      setCampaignLogs((l) => [
        `🏆 ĐẠI THẮNG! Kế sách "${tacticDesc}" thành công rực rỡ, phá tan quan ải ${selectedTarget.name}, thu phục sứ quân ${selectedTarget.warlordName}! Tổn thất ${lossSoldiers} quân sĩ. Uy danh +15!`,
        ...l.slice(0, 4),
      ]);

      if (nextConquered === 12) {
        api.saveGameProgress('su-quan', { result: 'unified' }, 'Thống nhất 12 Sứ Quân — Đại Cồ Việt').catch(() => {});
      }
    } else {
      setSoldiers((s) => Math.max(500, s - lossSoldiers * 1.4));
      setPrestige((p) => Math.max(20, p - 8));
      setCampaignLogs((l) => [
        `⚠️ Công thành bất lợi tại ${selectedTarget.name}! Quân phòng thủ kháng cự ác liệt, đành lui binh chỉnh đốn. Thiệt hại ${Math.floor(lossSoldiers * 1.4)} quân.`,
        ...l.slice(0, 4),
      ]);
    }

    setTacticsModal(false);
    setSelectedTarget(null);
  };

  return (
    <div className="max-w-md mx-auto p-3 space-y-3 select-none" style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}>
      {/* Top Header */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-800">
        <button
          onClick={onBackToHub}
          className="px-3.5 py-2 text-sm font-bold bg-slate-800/90 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center space-x-1.5 active:scale-95 transition-transform"
        >
          <span className="text-base">←</span>
          <span>GameHub</span>
        </button>

        <div className="flex items-center space-x-2">
          <span className="text-base font-black text-amber-400 tracking-wide flex items-center space-x-1.5">
            <span>⚔️</span>
            <span>LOẠN 12 SỨ QUÂN</span>
          </span>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30">
            CHIẾN DỊCH
          </span>
        </div>
      </div>

      {/* Leader & State HUD */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 space-y-2 text-xs">
        <div className="flex items-center justify-between font-bold">
          <div className="flex items-center space-x-2">
            <span className="text-xl">👑</span>
            <div>
              <h3 className="text-sm text-amber-300 font-black">Vạn Thắng Vương Đinh Bộ Lĩnh</h3>
              <p className="text-[11px] text-slate-400">Đại bản doanh: Động Hoa Lư (Ninh Bình)</p>
            </div>
          </div>
          <div className="text-right">
            <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded font-black border border-emerald-500/30">
              {conqueredCount}/12 Châu
            </span>
          </div>
        </div>

        {/* Resources Metrics */}
        <div className="grid grid-cols-3 gap-2 pt-1 font-bold text-center">
          <div className="bg-slate-950 p-2 rounded-lg border border-slate-800">
            <span className="text-slate-400 block text-[10px]">Binh Lực</span>
            <span className="text-rose-400 text-sm">🛡️ {soldiers.toLocaleString()}</span>
          </div>
          <div className="bg-slate-950 p-2 rounded-lg border border-slate-800">
            <span className="text-slate-400 block text-[10px]">Lương Thảo</span>
            <span className="text-amber-400 text-sm">🌾 {food.toLocaleString()}</span>
          </div>
          <div className="bg-slate-950 p-2 rounded-lg border border-slate-800">
            <span className="text-slate-400 block text-[10px]">Uy Danh</span>
            <span className="text-cyan-300 text-sm">🌟 {prestige}</span>
          </div>
        </div>
      </div>

      {/* Strategic Actions: Farming, Recruiting */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={handleFarming}
          className="py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-amber-300 font-bold text-xs flex items-center justify-center space-x-1.5 border border-slate-700 active:scale-95 transition-all shadow"
        >
          <span>🌾</span>
          <span>Khẩn Hoang (+Lương)</span>
        </button>

        <button
          onClick={handleRecruit}
          className="py-2.5 px-3 rounded-xl bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-500 hover:to-amber-500 text-white font-bold text-xs flex items-center justify-center space-x-1.5 shadow active:scale-95 transition-all"
        >
          <span>🛡️</span>
          <span>Chiêu Binh Mãi Mã</span>
        </button>
      </div>

      {/* 12 Sứ Quân Map Grid */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3 space-y-2 shadow-xl">
        <div className="flex justify-between items-center text-xs font-bold text-slate-300">
          <span>BẢN ĐỒ 12 CHÂU THỜI TIỀN LÊ (944-968):</span>
          <span className="text-amber-400 text-[11px]">Chạm để thảo phạt</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {provinces.map((prov) => (
            <button
              key={`prov_${prov.id}`}
              onClick={() => {
                if (!prov.isConquered) {
                  setSelectedTarget(prov);
                  setTacticsModal(true);
                }
              }}
              className={`p-2.5 rounded-xl border text-left transition-all relative ${
                prov.isConquered
                  ? 'bg-emerald-950/40 border-emerald-500/50 text-slate-200 shadow'
                  : 'bg-slate-800/80 border-slate-700 hover:border-amber-500 text-slate-300 active:scale-95'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg">{prov.avatar}</span>
                {prov.isConquered ? (
                  <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 font-black">
                    ✓ Quy phục
                  </span>
                ) : (
                  <span className="text-[10px] text-rose-400 font-bold">
                    {prov.garrison} quân
                  </span>
                )}
              </div>
              <h4 className="font-black text-xs text-slate-100 truncate">{prov.name}</h4>
              <p className="text-[10px] text-amber-400 truncate">{prov.warlordName}</p>
              <p className="text-[9px] text-slate-400 truncate">{prov.location}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Victory Declaration */}
      {isUnified && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-emerald-500/20 border border-amber-500/50 text-center space-y-2 animate-bounce">
          <div className="text-3xl">👑🇻🇳</div>
          <h3 className="text-base font-black text-amber-300">ĐẠI NGHIỆP HOÀN THÀNH — VẠN THẮNG VƯƠNG LÊN NGÔI</h3>
          <p className="text-xs text-slate-200">
            Đinh Bộ Lĩnh chính thức đăng quang Đinh Tiên Hoàng Đế, đặt quốc hiệu Đại Cồ Việt, định đô Hoa Lư, thái bình thịnh trị muôn đời!
          </p>
        </div>
      )}

      {/* Campaign Log Stream */}
      <div className="bg-slate-900/60 p-2.5 rounded-xl border border-slate-800 text-xs space-y-1">
        <span className="text-slate-400 font-bold block text-[11px]">📜 Nhật ký chinh phạt giang sơn:</span>
        {campaignLogs.map((log, idx) => (
          <p key={`log_${idx}`} className="text-[11px] text-slate-300 leading-snug">
            {log}
          </p>
        ))}
      </div>

      {/* Attack Tactics Modal */}
      {tacticsModal && selectedTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-amber-500/40 rounded-2xl p-5 max-w-sm w-full space-y-4 shadow-2xl">
            <div className="flex items-center space-x-3 pb-2 border-b border-slate-800">
              <span className="text-3xl">{selectedTarget.avatar}</span>
              <div>
                <h3 className="text-base font-black text-amber-300">THẢO PHẠT {selectedTarget.name.toUpperCase()}</h3>
                <p className="text-xs text-slate-300">
                  Sứ quân: <strong>{selectedTarget.warlordName}</strong> ({selectedTarget.garrison} quân đồn trú)
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-400">
              Chọn sách lược công thành để xuất binh:
            </p>

            <div className="space-y-2">
              <button
                onClick={() => executeAttack('frontal')}
                className="w-full p-3 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-left active:scale-98 transition-all"
              >
                <div className="font-bold text-xs text-rose-300">⚔️ Đột Kích Vỗ Mặt</div>
                <div className="text-[11px] text-slate-400">Tiến công chớp nhoáng phá cổng thành. Thích hợp khi binh lực áp đảo.</div>
              </button>

              <button
                onClick={() => executeAttack('ambush')}
                className="w-full p-3 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-left active:scale-98 transition-all"
              >
                <div className="font-bold text-xs text-cyan-300">🌊 Phục Kích Ven Sông</div>
                <div className="text-[11px] text-slate-400">Lợi dụng sông ngòi chặn đường tiếp tế, giảm thiểu thương vong binh sĩ.</div>
              </button>

              <button
                onClick={() => executeAttack('siege')}
                className="w-full p-3 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-left active:scale-98 transition-all"
              >
                <div className="font-bold text-xs text-amber-300">🛡️ Vây Thành Bức Hàng (-2,000 Lương)</div>
                <div className="text-[11px] text-slate-400">Tiêu hao lương thực để buộc đối phương dâng thành quy phục an toàn.</div>
              </button>
            </div>

            <button
              onClick={() => setTacticsModal(false)}
              className="w-full py-2.5 rounded-xl bg-slate-800 text-slate-400 hover:text-slate-200 font-bold text-xs active:scale-95"
            >
              Hủy Bỏ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
